import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useTours } from '../../hooks/useTours';
import { Tour } from '../../types';
import { Pause, Play } from 'lucide-react';

const DEFAULT_HERO_IMAGE = '/images/default_bg.jpeg';
const FALLBACK_SCRIPTS = [
  {
    lang: 'en',
    dir: 'ltr',
    fontFamily: "'Playfair Display', Georgia, serif",
  },
  {
    lang: 'zh-Hans',
    dir: 'ltr',
    fontFamily: "'Noto Serif SC', 'Songti SC', serif",
  },
  {
    lang: 'kn',
    dir: 'ltr',
    fontFamily: "'Noto Serif Kannada', serif",
  },
  {
    lang: 'ar',
    dir: 'rtl',
    fontFamily: "'Amiri', serif",
  },
  {
    lang: 'ru',
    dir: 'ltr',
    fontFamily: "'Playfair Display', Georgia, serif",
  },
  {
    lang: 'th',
    dir: 'ltr',
    fontFamily: "'Noto Serif Thai', serif",
  },
] as const;

const FALLBACK_TAGLINES = {
  en: [
    'Discover Amazing Destinations',
    'Travel the World',
    'Explore Hidden Wonders',
    'Journey Beyond Borders',
    'Wander Without Limits',
  ],
  'zh-Hans': [
    '探索奇妙目的地',
    '环游世界',
    '探索隐秘奇观',
    '跨越国界去旅行',
    '无界漫游',
  ],
  kn: [
    'ಅದ್ಭುತ ತಾಣಗಳನ್ನು ಅನ್ವೇಷಿಸಿ',
    'ಜಗತ್ತನ್ನು ಸಂಚರಿಸಿ',
    'ಮರೆಯಾದ ಅದ್ಭುತಗಳನ್ನು ಅನ್ವೇಷಿಸಿ',
    'ಗಡಿಗಳಾಚೆಗೆ ಪ್ರಯಾಣಿಸಿ',
    'ಮಿತಿಯಿಲ್ಲದೆ ಅಲೆದಾಡಿ',
  ],
  ar: [
    'اكتشف وجهات مذهلة',
    'سافر حول العالم',
    'استكشف العجائب الخفية',
    'ارتحل عبر الحدود',
    'تجوّل بلا حدود',
  ],
  ru: [
    'Откройте удивительные места',
    'Путешествуйте по миру',
    'Откройте скрытые чудеса',
    'Путешествуйте за границы',
    'Странствуйте без границ',
  ],
  th: [
    'ค้นพบจุดหมายที่น่าทึ่ง',
    'ท่องเที่ยวรอบโลก',
    'สำรวจสิ่งมหัศจรรย์ที่ซ่อนอยู่',
    'ท่องเที่ยวข้ามพรมแดน',
    'ท่องไปอย่างไร้ขีดจำกัด',
  ],
} as const;

const FALLBACK_HEADLINES = FALLBACK_TAGLINES.en.flatMap((_, taglineIndex) =>
  FALLBACK_SCRIPTS.map((script) => ({
    ...script,
    text: FALLBACK_TAGLINES[script.lang][taglineIndex],
    key: `${script.lang}-${taglineIndex}`,
  }))
);
const FALLBACK_HEADLINE_INTERVAL_MS = 5000;

interface TourSlideshowProps {
  onTourSelect?: (tour: Tour) => void;
}

// Predefined color palettes for different image types
const colorPalettes = {
  warm: ['text-orange-400', 'text-yellow-400', 'text-red-400', 'text-pink-400'],
  cool: ['text-blue-400', 'text-cyan-400', 'text-indigo-400', 'text-purple-400'],
  earth: ['text-green-400', 'text-emerald-400', 'text-teal-400', 'text-lime-400'],
  neutral: ['text-gray-300', 'text-slate-300', 'text-zinc-300', 'text-stone-300'],
  vibrant: ['text-accent-champagne', 'text-yellow-300', 'text-orange-300', 'text-pink-300']
};

const TourSlideshow: React.FC<TourSlideshowProps> = ({ onTourSelect }) => {
  const { tours, loading, error } = useTours(10);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [fallbackHeadlineIndex, setFallbackHeadlineIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState<Set<string>>(new Set());
  const [subheadingColor, setSubheadingColor] = useState('text-accent-champagne');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // Parallax effect for background images - more pronounced
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  // Function to determine font size based on tagline length
  const getTaglineFontSize = useCallback((tagline: string) => {
    // Count separators that often appear in multi-part taglines
    const separators = [',', ' and ', ' & ', ' AND ', ' And '];
    let separatorCount = 0;
    
    separators.forEach(sep => {
      const matches = tagline.split(sep).length - 1;
      separatorCount += matches;
    });
    
    const partCount = separatorCount + 1;
    const taglineLength = tagline.length;
    
    if (partCount >= 3 || taglineLength > 50) {
      return 'text-2xl md:text-3xl lg:text-4xl';
    } else if (partCount === 2 || taglineLength > 30) {
      return 'text-2xl md:text-3xl lg:text-4xl xl:text-5xl';
    } else {
      return 'text-3xl md:text-4xl lg:text-5xl';
    }
  }, []);


  // Filter tours that have banner images (memoized for performance)
  const toursWithBanners = useMemo(() => 
    tours.filter(tour => 
      tour.imageUrl && 
      tour.imageUrl !== '/images/bg_raise_logo.png' && 
      tour.status === 'active'
    ), [tours]
  );

  const showFallbackHero = !loading && (Boolean(error) || toursWithBanners.length === 0);

  useEffect(() => {
    if (!showFallbackHero) {
      return;
    }

    const intervalId = setInterval(() => {
      setFallbackHeadlineIndex((prev) => (prev + 1) % FALLBACK_HEADLINES.length);
    }, FALLBACK_HEADLINE_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [showFallbackHero]);

  // Function to analyze image and determine color palette
  const analyzeImageColor = useCallback((imageUrl: string) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        canvas.width = 100;
        canvas.height = 100;
        ctx.drawImage(img, 0, 0, 100, 100);
        
        const imageData = ctx.getImageData(0, 0, 100, 100);
        const data = imageData.data;
        
        let r = 0, g = 0, b = 0;
        const pixelCount = data.length / 4;
        
        for (let i = 0; i < data.length; i += 4) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
        }
        
        r = Math.floor(r / pixelCount);
        g = Math.floor(g / pixelCount);
        b = Math.floor(b / pixelCount);
        
        // Determine dominant color characteristics
        const maxColor = Math.max(r, g, b);
        const minColor = Math.min(r, g, b);
        const saturation = (maxColor - minColor) / maxColor;
        const brightness = (r + g + b) / 3;
        
        // Select palette based on color analysis
        let selectedPalette;
        if (brightness < 100) {
          // Dark images - use vibrant colors
          selectedPalette = colorPalettes.vibrant;
        } else if (r > g && r > b && r - g > 50) {
          // Red/orange dominant - warm palette
          selectedPalette = colorPalettes.warm;
        } else if (b > r && b > g && b - r > 50) {
          // Blue dominant - cool palette
          selectedPalette = colorPalettes.cool;
        } else if (g > r && g > b && g - r > 50) {
          // Green dominant - earth palette
          selectedPalette = colorPalettes.earth;
        } else if (saturation < 0.3) {
          // Low saturation - neutral palette
          selectedPalette = colorPalettes.neutral;
        } else {
          // Default to vibrant for colorful images
          selectedPalette = colorPalettes.vibrant;
        }
        
        // Randomly select from the chosen palette
        const randomColor = selectedPalette[Math.floor(Math.random() * selectedPalette.length)];
        setSubheadingColor(randomColor);
      } catch (error) {
        // Fallback to default color
        setSubheadingColor('text-accent-champagne');
      }
    };
    
    img.onerror = () => {
      setSubheadingColor('text-accent-champagne');
    };
    
    img.src = imageUrl;
  }, []);

  // Preload images for better performance
  useEffect(() => {
    const preloadImage = (src: string) => {
      if (preloadedImages.has(src)) return;
      
      const img = new Image();
      img.onload = () => {
        setPreloadedImages(prev => {
          const newSet = new Set(prev);
          newSet.add(src);
          return newSet;
        });
      };
      img.src = src;
    };

    // Preload current and next image
    if (toursWithBanners[currentSlide]) {
      preloadImage(toursWithBanners[currentSlide].imageUrl);
    }
    if (toursWithBanners[(currentSlide + 1) % toursWithBanners.length]) {
      preloadImage(toursWithBanners[(currentSlide + 1) % toursWithBanners.length].imageUrl);
    }
  }, [currentSlide, toursWithBanners, preloadedImages]);

  // Analyze current slide image for color
  useEffect(() => {
    if (toursWithBanners[currentSlide]) {
      analyzeImageColor(toursWithBanners[currentSlide].imageUrl);
    }
  }, [currentSlide, toursWithBanners, analyzeImageColor]);

  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  // Auto-play functionality with pause control
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only start autoplay if conditions are met
    if (!isHovered && !isPaused && toursWithBanners.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % toursWithBanners.length);
      }, 5000); // 5 seconds
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isHovered, isPaused, toursWithBanners.length]);

  // Handle slide navigation (without triggering tour selection)
  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  // Handle tour selection (for main CTA button)
  const handleTourSelect = useCallback((tour: Tour) => {
    if (onTourSelect) {
      onTourSelect(tour);
    }
  }, [onTourSelect]);

  // Remove wheel scroll navigation - only allow automatic and manual selection

  // Remove keyboard navigation - only allow automatic and manual selection

  // Remove touch navigation - only allow automatic and manual selection

  if (loading) {
    return (
      <section className="hero-section">
        <div className="hero-background">
          <img
            src={DEFAULT_HERO_IMAGE}
            alt=""
            className="hero-image active"
          />
        </div>
        <div className="hero-overlay" />
        <div className="flex items-center justify-center h-full relative z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-champagne mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading amazing destinations...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || toursWithBanners.length === 0) {
    return (
      <section className="hero-section">
        <div className="hero-background">
          <img
            src={DEFAULT_HERO_IMAGE}
            alt=""
            className="hero-image active"
          />
        </div>
        <div className="hero-overlay" />
        <div className="hero-content">
          <AnimatePresence mode="wait">
            <motion.h1
              key={FALLBACK_HEADLINES[fallbackHeadlineIndex].key}
              lang={FALLBACK_HEADLINES[fallbackHeadlineIndex].lang}
              dir={FALLBACK_HEADLINES[fallbackHeadlineIndex].dir}
              className="heading-hero text-white"
              style={{ fontFamily: FALLBACK_HEADLINES[fallbackHeadlineIndex].fontFamily }}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
            >
              {FALLBACK_HEADLINES[fallbackHeadlineIndex].text}
            </motion.h1>
          </AnimatePresence>
        </div>
      </section>
    );
  }

  const currentTour = toursWithBanners[currentSlide];

  return (
    <section 
      ref={containerRef}
      className="hero-section relative overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background Images */}
      <div className="absolute inset-0 -z-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.75, ease: "easeInOut" }}
            style={{ y }}
          >
            <img
              src={currentTour.imageUrl}
              alt={currentTour.tourName}
              className="w-full h-full object-cover"
              loading="eager"
              decoding="async"
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 -z-10 gradient-overlay-dark" />

      {/* Vertical Destination Menu */}
      <div className="absolute left-8 top-1/2 -translate-y-1/2 z-20 hidden lg:block">
        <div className="flex flex-col items-center space-y-6">
          {/* Vertical Line */}
          <div className="w-0.5 h-32 bg-white/30"></div>
          
          {/* Destination Dots */}
          <div className="flex flex-col space-y-4">
            {toursWithBanners.map((tour, index) => (
              <motion.button
                key={tour.tourId}
                onClick={() => goToSlide(index)}
                className={`relative group cursor-pointer transition-all duration-300 ${
                  index === currentSlide ? 'scale-125' : 'scale-100'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {/* Glowing Ring for Active */}
                {index === currentSlide && (
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-accent-champagne"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1.5, opacity: 0.6 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
                
                {/* Dot */}
                <div className={`w-4 h-4 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-accent-champagne/80 shadow-lg shadow-accent-champagne/50' 
                    : 'bg-white/20 hover:bg-white/40'
                }`} />
                
                {/* Tooltip with Glass Effect */}
                <div className="absolute left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="bg-white/20 backdrop-blur-md text-white px-6 py-4 rounded-full text-lg font-semibold whitespace-nowrap border border-white/20">
                    {tour.tourName}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
          
          {/* Vertical Line */}
          <div className="w-0.5 h-32 bg-white/30"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="hero-content relative z-10">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center space-y-8"
        >
          {/* Tour Name */}
          <motion.h1
            className="heading-hero text-white mb-6 text-6xl md:text-7xl lg:text-8xl xl:text-9xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            {currentTour.tourName}
          </motion.h1>

          {/* Tour Tagline */}
          {currentTour.tagline?.trim() && (
            <motion.h2
              className={`${getTaglineFontSize(currentTour.tagline)} font-light ${subheadingColor} mb-4`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {currentTour.tagline.toUpperCase()}
            </motion.h2>
          )}


          {/* Call to Action Button */}
          <motion.div
            className="flex justify-center items-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <button
              onClick={() => handleTourSelect(currentTour)}
              className="bg-white/20 hover:bg-white/30 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 group backdrop-blur-sm border border-white/20 text-lg"
            >
              Explore &gt;
            </button>
          </motion.div>
        </motion.div>
      </div>


      {/* Pause/Play Button */}
      <motion.button
        className={`absolute bottom-8 right-8 text-white p-4 rounded-full transition-all duration-300 backdrop-blur-sm border group ${
          isPaused 
            ? 'bg-green-500/30 hover:bg-green-500/50 border-green-400/20' 
            : 'bg-black/30 hover:bg-black/50 border-white/20'
        }`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.4 }}
        onClick={togglePause}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        {isPaused ? (
          <Play className="w-6 h-6" />
        ) : (
          <Pause className="w-6 h-6" />
        )}
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-black/80 text-white text-xs px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
          {isPaused ? 'Click to play slideshow' : 'Click to pause slideshow'}
        </div>
      </motion.button>

      {/* Mobile Navigation */}
      <div className="lg:hidden absolute bottom-4 left-4 right-4 flex justify-between items-center">
        <button
          onClick={() => setCurrentSlide(prev => (prev - 1 + toursWithBanners.length) % toursWithBanners.length)}
          className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
        >
          ←
        </button>
        
        <div className="flex space-x-2">
          {toursWithBanners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide ? 'bg-accent-champagne/80' : 'bg-white/20'
              }`}
            />
          ))}
        </div>
        
        <button
          onClick={() => setCurrentSlide(prev => (prev + 1) % toursWithBanners.length)}
          className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
        >
          →
        </button>
      </div>
    </section>
  );
};

export default TourSlideshow;
